import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Text, Button, Divider, Image
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import FormValidator from 'utils/FormValidator';
import UrlUtils from 'utils/Url';
import userUtils from 'utils/User';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import ErrorList from 'components/ErrorList';
import Markdown from 'components/Markdown/Markdown';

import { space } from 'style/config';
import RegisterEN from 'components/Register/locales/Register_en_US';
import RegisterFR from 'components/Register/locales/Register_fr_CA';
const getText = localeUtils.getLocalisedDataFromResource(RegisterEN, RegisterFR);

const RegisterEmailLookup = ({ handleSignIn, handleEmailChange, handleStoreMember, handleLookup }) => {
    const dispatch = useDispatch();
    const emailInput = useRef(null);
    const [errorMessages, setErrorMessages] = useState([]);

    const isValidLookup = () => {
        const fieldsForValidation = [emailInput.current];
        const errors = FormValidator.getErrors(fieldsForValidation);

        return !errors.fields.length;
    };

    const handleEmailLookup = async () => {
        const email = emailInput.current?.getValue();
        const successCallback = data => {
            if (data.isStoreBiMember) {
                handleStoreMember(true);
            } else {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set('existingUser', email || '');
                UrlUtils.redirectTo(`/login?${urlParams.toString()}`);
            }
        };

        const failureCallback = error => {
            if (userUtils.isEmailDisposableError(error)) {
                emailInput.current?.showError(getText('invalidEmailError'));
            } else if (userUtils.isEmailTypoError(error)) {
                dispatch(
                    Actions.showEmailTypoModal({
                        isOpen: true,
                        isNewUserFlow: true,
                        email,
                        onContinue: () => handleStoreMember(true)
                    })
                );
            } else if (userUtils.isEmailVerifyError(error)) {
                handleStoreMember(true);
            } else {
                handleLookup(false);
            }
        };

        await dispatch(UserActions.checkUser(email, successCallback, failureCallback));
    };

    const handleLookupSubmit = async e => {
        e.preventDefault();
        setErrorMessages([]);

        if (!isValidLookup()) {
            return;
        }

        try {
            await handleEmailLookup();
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || getText('genericErrorMessage');

            setErrorMessages([errorMessage]);
        }
    };

    return (
        <Box
            lineHeight='tight'
            is='form'
            onSubmit={handleLookupSubmit}
            noValidate
        >
            <Text
                is='h2'
                fontWeight='bold'
                fontSize='xl'
                marginTop={2}
                children={getText('title')}
            />
            <Image
                alt='Beauty Insider'
                disableLazyLoad={true}
                display='block'
                src='/img/ufe/bi/logo-beauty-insider.svg'
                width={194}
                height={32}
                marginBottom={4}
                marginTop={5}
            />

            <Markdown
                marginBottom={4}
                content={getText('joinBiFreeShip')}
            />

            {errorMessages.length > 0 && (
                <Box css={styles.errorContainer}>
                    <ErrorList errorMessages={errorMessages} />
                </Box>
            )}

            <InputEmail
                marginBottom={4}
                required={true}
                name='email'
                label={getText('emailAddresLabel')}
                hideAsterisk={true}
                ref={emailInput}
                onChange={handleEmailChange}
            />
            <Button
                variant='primary'
                width='100%'
                type='submit'
            >
                {getText('confirmButton')}
            </Button>
            <Divider marginY={4} />
            <b>{getText('alreadyHaveAccount')}</b>
            <Button
                width='172'
                display='block'
                variant='secondary'
                marginTop={3}
                paddingX={7}
                onClick={handleSignIn}
            >
                {getText('signIn')}
            </Button>
        </Box>
    );
};

const styles = {
    errorContainer: {
        marginBottom: space[4]
    }
};

export default wrapFunctionalComponent(RegisterEmailLookup, 'RegisterEmailLookup');
